import { useLocation, useNavigate } from "react-router-dom";
import NextStopTableScreenScreen from "./NextStopVehicleScreenScreen";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import config from "../../config.json";
import axios from "axios";
// import BusIcon from "../../images/bus-svgrepo-com.svg";
import ActiveVehicleIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import DefaultVehicleIcon from "../../images/bus-svgrepo-com.svg";
import { CircularProgress } from "@mui/material";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import WestOutlinedIcon from '@mui/icons-material/WestOutlined';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';

const mapContainerStyle = {
  width: "100%",
  height: "90.5vh",
};
const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];
const libraries = ["places", "directions"];
const POSITION_HISTORY_SIZE = 25;
const DIRECTION_CHECK_THRESHOLD = 4;
const MOVEMENT_THRESHOLD = 10;
const TERMINAL_THRESHOLD = 20;
const LOCATION_FETCH_INTERVAL = 10000;
const STOP_DETAILS_FETCH_INTERVAL = 50000;
const STAGNATION_CHECK_INTERVAL = 300000;
const OFF_ROUTE_THRESHOLD = 50;
const STAGNATION_DISTANCE_THRESHOLD = 5;

const VehicleScreen = () => {
  const [position, setPosition] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [userState, setUserState] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [directions, setDirections] = useState(null);
  const [stopDetails, setStopDetails] = useState({});
  const [previousPosition, setPreviousPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const [map, setMap] = useState(null);
  const [snappedPosition, setSnappedPosition] = useState(null);
  const [routePaths, setRoutePaths] = useState({ before: [], after: [] });
  const [isInbound, setIsInbound] = useState(false);
  const [isMoving, setIsMoving] = useState(true);
  const [lastMovedPosition, setLastMovedPosition] = useState(null);
  const [stoppedDuration, setStoppedDuration] = useState(0);
  const [positionHistory, setPositionHistory] = useState([]);
  const [fullPath, setFullPath] = useState([]);
  const [pathInitialized, setPathInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isOffRoute, setIsOffRoute] = useState(false);
  const [offRouteDuration, setOffRouteDuration] = useState(0);
  const [pathDirection, setPathDirection] = useState(null);
  const [lastPosition, setLastPosition] = useState(null);
  const [vehicleStatus, setVehicleStatus] = useState("ACTIVE");
  const [isReconnecting, setIsReconnecting] = useState(false);

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;
  const isMountedRef = useRef(true);
  const directionCheckRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const directionsRef = useRef(directions);
  const eventSourceRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 25;
  const reconnectInterval = 8000;

  useEffect(() => {
    directionsRef.current = directions;
  }, [directions]);

  const [vehicleId, setVehicleId] = useState(null);
  const [organizationId, setOrganizationId] = useState(17);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const vId = urlParams.get("vehicleId");
    const orgId = urlParams.get("organizationId");

    if (vId) {
      setVehicleId(vId);
    } else {
      const pathParts = window.location.pathname.split("/");
      const lastPart = pathParts[pathParts.length - 1];
      if (!isNaN(parseInt(lastPart))) {
        setVehicleId(lastPart);
      }
    }

    if (orgId) {
      setOrganizationId(parseInt(orgId));
    }
  }, []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyD7rqUpTzUpEbxm-Xc7ikltFJGzOhd92Qk",
    libraries,
  });

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const getRouteById = async (id) => {
    try {
      const resp = await axios.get(`${config.baseURL}/route/id`, {
        params: { id },
        withCredentials: true,
      });
      return resp.data;
    } catch (error) {
      console.error("Error fetching route:", error);
      return null;
    }
  };

  const fetchStopsForRoute = async (route) => {
    if (!route?.routeCoordinates) return [];
    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const stopsResponse = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              organizationId: 17,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          }
        );
        stops.push(stopsResponse.data);
      } catch (error) {
        console.error("Error fetching stops:", error);
      }
    }
    return stops;
  };

  useEffect(() => {
    const fetchVehicleData = async () => {
      if (!vehicleId) return;
      try {
        const response = await axios.get(`${config.baseURL}/vehicle/id`, {
          params: {
            vehicleId: vehicleId,
            organizationId: orgId,
          },
          withCredentials: true,
        });
        const vehicleData = response.data.vehicle;
        setVehicle(vehicleData);
        setVehicleStatus(vehicleData.status || "ACTIVE");
        const routeData = await getRouteById(vehicleData.routeId);
        if (routeData) {
          setRoute(routeData);
          const stopsData = await fetchStopsForRoute(routeData);
          setStops(stopsData);
        }
      } catch (error) {
        console.error("Error fetching vehicle data:", error);
      }
    };
    fetchVehicleData();
  }, [vehicleId, organizationId]);

  const visibleStops = stops.filter(stop => {
    if (pathDirection === "source_to_dest") {
      return stop.name !== "Manipal Hospital";
    } else if (pathDirection === "dest_to_source") {
      return stop.name !== "BIAL-T1-Departure";
    }
    return true; // Show all stops if pathDirection is null or undefined
  });

  const calculateDistance = useCallback((p1, p2) => {
    if (!p1 || !p2) return Infinity;
    const R = 6371e3;
    const φ1 = (p1.lat * Math.PI) / 180;
    const φ2 = (p2.lat * Math.PI) / 180;
    const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
    const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  const findNearestPointOnRoute = useCallback(
    (position, directions) => {
      if (!position || !directions?.routes?.[0]) return null;
      const route = directions.routes[0];
      let closestPoint = null;
      let minDistance = Infinity;
      let routeIndex = -1;
      let currentRouteIndex = 0;

      route.legs.forEach((leg) => {
        leg.steps.forEach((step) => {
          const path = step.path;
          path.forEach((point, index) => {
            if (index === 0) return;
            const start = {
              lat: path[index - 1].lat(),
              lng: path[index - 1].lng(),
            };
            const end = {
              lat: point.lat(),
              lng: point.lng(),
            };
            const dx = end.lng - start.lng;
            const dy = end.lat - start.lat;
            const segmentLength = Math.sqrt(dx * dx + dy * dy);
            if (segmentLength === 0) return;

            const t =
              ((position.lng - start.lng) * dx +
                (position.lat - start.lat) * dy) /
              (segmentLength * segmentLength);

            if (t < 0) {
              const distToStart = calculateDistance(position, start);
              if (distToStart < minDistance) {
                minDistance = distToStart;
                closestPoint = start;
                routeIndex = currentRouteIndex;
              }
              return;
            }

            if (t > 1) {
              const distToEnd = calculateDistance(position, end);
              if (distToEnd < minDistance) {
                minDistance = distToEnd;
                closestPoint = end;
                routeIndex = currentRouteIndex + 1;
              }
              return;
            }

            const projectedPoint = {
              lat: start.lat + t * dy,
              lng: start.lng + t * dx,
            };
            const distance = calculateDistance(position, projectedPoint);
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = projectedPoint;
              routeIndex = currentRouteIndex + t;
            }

            currentRouteIndex++;
          });
        });
      });

      const MAX_SNAP_DISTANCE = 50;
      if (minDistance <= MAX_SNAP_DISTANCE) {
        return {
          ...closestPoint,
          routeIndex: routeIndex,
        };
      }
      return null;
    },
    [calculateDistance]
  );

  const detectVehicleMovement = useCallback(
    (newPosition, lastPosition) => {
      if (!lastPosition || !newPosition) return true;
      const distance = calculateDistance(newPosition, lastPosition);
      return distance > MOVEMENT_THRESHOLD;
    },
    [calculateDistance]
  );

  const extractPathPoints = useCallback((directions) => {
    if (!directions?.routes?.[0]?.legs) return [];
    let points = [];
    directions.routes[0].legs.forEach((leg) => {
      leg.steps.forEach((step) => {
        const path = step.path;
        path.forEach((point) => {
          points.push({
            lat: point.lat(),
            lng: point.lng(),
          });
        });
      });
    });
    return points;
  }, []);

  const isJourneyComplete = useCallback(
    (currentPosition) => {
      if (!stops || stops.length === 0 || !currentPosition || !pathDirection) {
        return false;
      }
      const finalStop =
        pathDirection === "source_to_dest" ? stops[stops.length - 1] : stops[0];
      const finalStopPosition = {
        lat: parseFloat(finalStop.latitude),
        lng: parseFloat(finalStop.longitude),
      };
      const distanceToFinal = calculateDistance(
        currentPosition,
        finalStopPosition
      );
      const TERMINAL_RADIUS = 50;
      const routeCompletionRatio =
        routePaths.before.length /
        (routePaths.before.length + routePaths.after.length);
      let isNearTerminalSection = false;
      if (fullPath.length > 0) {
        const terminalSectionStartIdx = Math.floor(fullPath.length * 0.9);
        const terminalSection = fullPath.slice(terminalSectionStartIdx);
        let minDistanceToTerminalSection = Infinity;
        terminalSection.forEach((point) => {
          const distance = calculateDistance(currentPosition, point);
          minDistanceToTerminalSection = Math.min(
            minDistanceToTerminalSection,
            distance
          );
        });
        isNearTerminalSection = minDistanceToTerminalSection < TERMINAL_RADIUS;
      }
      const isCloseToFinalStop = distanceToFinal < TERMINAL_RADIUS;
      const hasCompletedMostOfRoute = routeCompletionRatio > 0.9;
      const isStoppedNearEnd =
        !isMoving &&
        (isCloseToFinalStop || isNearTerminalSection) &&
        stoppedDuration > 30;
      const isComplete =
        (isCloseToFinalStop && hasCompletedMostOfRoute) ||
        (isNearTerminalSection && hasCompletedMostOfRoute) ||
        isStoppedNearEnd;
      return isComplete;
    },
    [
      stops,
      pathDirection,
      calculateDistance,
      routePaths.before,
      routePaths.after,
      fullPath,
      isMoving,
      stoppedDuration,
    ]
  );

  const checkDirectionChange = useCallback(
    (positions) => {
      if (
        positions.length < DIRECTION_CHECK_THRESHOLD ||
        !stops ||
        stops.length < 2
      ) {
        return null;
      }
      const source = {
        lat: parseFloat(stops[0].latitude),
        lng: parseFloat(stops[0].longitude),
      };
      const destination = {
        lat: parseFloat(stops[stops.length - 1].latitude),
        lng: parseFloat(stops[stops.length - 1].longitude),
      };
      const firstPos = positions[0];
      const lastPos = positions[positions.length - 1];
      const firstPosToSource = calculateDistance(firstPos, source);
      const firstPosToDest = calculateDistance(firstPos, destination);
      const lastPosToSource = calculateDistance(lastPos, source);
      const lastPosToDest = calculateDistance(lastPos, destination);
      const sourceDistanceChange = lastPosToSource - firstPosToSource;
      const destDistanceChange = lastPosToDest - firstPosToDest;

      if (
        Math.abs(sourceDistanceChange) > 10 ||
        Math.abs(destDistanceChange) > 10
      ) {
        if (sourceDistanceChange < 0 && destDistanceChange > 0) {
          return "dest_to_source";
        } else if (sourceDistanceChange > 0 && destDistanceChange < 0) {
          return "source_to_dest";
        }
      }
      return null;
    },
    [stops, calculateDistance]
  );

  const splitRouteAtPosition = useCallback(
    (allPoints, position) => {
      if (!allPoints.length || !position) {
        return { before: [], after: [] };
      }
      if (!pathDirection) {
        return { before: allPoints, after: [] };
      }
      let splitIndex = 0;
      let minDistance = Infinity;
      allPoints.forEach((point, index) => {
        const distance = calculateDistance(position, point);
        if (distance < minDistance) {
          minDistance = distance;
          splitIndex = index;
        }
      });
      if (pathDirection === "source_to_dest") {
        return {
          before: allPoints.slice(0, splitIndex + 1),
          after: allPoints.slice(splitIndex),
        };
      } else {
        return {
          before: allPoints.slice(splitIndex),
          after: allPoints.slice(0, splitIndex + 1),
        };
      }
    },
    [pathDirection, calculateDistance]
  );


  const fetchLocation = useCallback(async () => {
    if (!vehicle?.device_id || eventSourceRef.current) return;
  
    const connectSSE = () => {
      const query = `vehicleId=${vehicle.device_id}`;
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;
  
      eventSource.onmessage = (event) => {
        try {
          const locationData = JSON.parse(event.data);
          console.log("SSE Data Received:", locationData);
          if (!isMountedRef.current) return;
  
          const newPosition = {
            lat: parseFloat(locationData?.Latitude || locationData?.latitude),
            lng: parseFloat(locationData?.Longitude || locationData?.longitude),
            timestamp: Date.now(),
          };
  
          if (!newPosition.lat || !newPosition.lng) {
            console.error("Invalid position data:", newPosition);
            return;
          }
  
          setIsOnline(true);
          setIsReconnecting(false);
          reconnectAttemptsRef.current = 0;
  
          axios
            .get(`${config.baseURL}/vehicle/id`, {
              params: {
                vehicleId: vehicleId,
                organizationId: orgId,
              },
              withCredentials: true,
            })
            .then((response) => {
              const newStatus = response.data.vehicle.status || "ACTIVE";
              setVehicleStatus(newStatus);
            })
            .catch((error) => {
              console.error("Error fetching vehicle status:", error);
            });
  
          let isCurrentlyOffRoute = false;
          if (directionsRef.current) {
            const snapped = findNearestPointOnRoute(
              newPosition,
              directionsRef.current
            );
            const distanceFromRoute = snapped
              ? calculateDistance(newPosition, snapped)
              : Infinity;
            isCurrentlyOffRoute = distanceFromRoute > OFF_ROUTE_THRESHOLD;
            console.log("Off-route check:", { isCurrentlyOffRoute, distanceFromRoute, snapped });
          }
  
          if (isCurrentlyOffRoute) {
            setIsOffRoute(true);
            setOffRouteDuration((prev) => prev + 10);
            if (offRouteDuration >= 300 && vehicle?.device_id) {
              localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
              setPathDirection(null);
              setPositionHistory([]);
              console.log("Cleared pathDirection due to long off-route stop");
            }
          } else {
            setIsOffRoute(false);
            setOffRouteDuration(0);
          }
  
          if (
            !isCurrentlyOffRoute &&
            detectVehicleMovement(newPosition, lastMovedPosition)
          ) {
            setPositionHistory((prev) => {
              const updated = [...prev, newPosition].slice(-POSITION_HISTORY_SIZE);
              console.log("Position History Updated:", updated);
              return updated;
            });
          }
  
          const speed = parseFloat(locationData?.Speed) || 0;
          setSpeed(speed);
          const moving = detectVehicleMovement(newPosition, lastMovedPosition);
          console.log("Movement Check:", { moving, speed, newPosition });
          if (moving) {
            setIsMoving(true);
            setLastMovedPosition(newPosition);
            setStoppedDuration(0);
          } else {
            setIsMoving(false);
            setStoppedDuration((prev) => prev + 10);
          }
  
          if (directionsRef.current && !isCurrentlyOffRoute) {
            const snapped = findNearestPointOnRoute(
              newPosition,
              directionsRef.current
            );
            if (snapped) {
              setPreviousPosition(position);
              setSnappedPosition(snapped);
              setPosition({
                ...newPosition,
                lat: snapped.lat,
                lng: snapped.lng,
              });
              console.log("Snapped Position:", snapped);
              if (previousPosition) {
                const dx = snapped.lng - previousPosition.lng;
                const dy = snapped.lat - previousPosition.lat;
                const angle = Math.atan2(dy, dx) * (180 / Math.PI);
                setHeading(angle);
              }
            } else if (
              position &&
              calculateDistance(position, newPosition) > MOVEMENT_THRESHOLD * 2
            ) {
              setPreviousPosition(position);
              setPosition(newPosition);
            }
          } else {
            setPosition(newPosition);
          }
  
          setLastPosition(newPosition);
          if (map) {
            map.panTo(newPosition);
          }
        } catch (error) {
          console.error("Error processing SSE data:", error);
        }
      };
  
      eventSource.onerror = () => {
        setIsOnline(false);
        setIsReconnecting(true);
        eventSource.close();
        eventSourceRef.current = null;
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(`Reconnecting SSE, attempt ${reconnectAttemptsRef.current}`);
          setTimeout(connectSSE, reconnectInterval);
        } else {
          console.error("Max reconnect attempts reached");
          setIsReconnecting(false);
        }
      };
    };
  
    connectSSE();
  
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [
    vehicle?.device_id,
    vehicleId,
    orgId,
    findNearestPointOnRoute,
    position,
    previousPosition,
    lastMovedPosition,
    detectVehicleMovement,
    offRouteDuration,
    calculateDistance,
    map,
  ])

  useEffect(() => {
    if (!vehicle?.device_id) return;
    isMountedRef.current = true;
    const cleanup = fetchLocation();
    return () => {
      isMountedRef.current = false;
      if (typeof cleanup === "function") cleanup();
    };
  }, [vehicle?.device_id, fetchLocation]);

  useEffect(() => {
    if (!vehicle?.device_id) return;

    const checkStagnation = async () => {
      try {
        const locationResponse = await fetch(
          `${config.baseURL}/location/latest/${vehicle.device_id}`
        );
        const locationData = await locationResponse.json();
        const currentPosition = {
          lat: parseFloat(locationData?.latitude),
          lng: parseFloat(locationData?.longitude),
        };

        const vehicleResponse = await axios.get(
          `${config.baseURL}/vehicle/id`,
          {
            params: {
              vehicleId: vehicleId,
              organizationId: orgId,
            },
            withCredentials: true,
          }
        );
        const currentStatus = vehicleResponse.data.vehicle.status || "ACTIVE";

        if (
          lastPosition &&
          calculateDistance(currentPosition, lastPosition) <=
            STAGNATION_DISTANCE_THRESHOLD &&
          ["INACTIVE", "DEAD"].includes(currentStatus)
        ) {
          console.log(
            `Resetting pathDirection due to stagnation (status: ${currentStatus})`
          );
          setPathDirection(null);
          setPositionHistory([]);
          localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
        }

        setLastPosition(currentPosition);
        setVehicleStatus(currentStatus);
      } catch (error) {
        console.error("Error during stagnation check:", error);
      }
    };

    const intervalId = setInterval(checkStagnation, STAGNATION_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [vehicle?.device_id, vehicleId, orgId, lastPosition, calculateDistance]);

  useEffect(() => {
    if (!isLoaded || !stops?.length || !route?.routeCoordinates) return;
    const directionsService = new window.google.maps.DirectionsService();
    const waypoints = stops.map((stop) => ({
      location: new window.google.maps.LatLng(
        parseFloat(stop.latitude),
        parseFloat(stop.longitude)
      ),
      stopover: true,
    }));
    const request = {
      origin: waypoints[0].location,
      destination: waypoints[waypoints.length - 1].location,
      waypoints: waypoints.slice(1, -1),
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };
    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);
        const allPoints = extractPathPoints(result);
        setFullPath(allPoints);
        console.log("Directions Set:", { allPointsLength: allPoints.length }); // Debug log
        if (!position && map) {
          map.setCenter(waypoints[0].location);
        }
      } else {
        console.error("Directions request failed:", status);
      }
    });
  }, [stops, route, isLoaded, position, map, extractPathPoints]);

  useEffect(() => {
    if (
      positionHistory.length < POSITION_HISTORY_SIZE ||
      !isMoving ||
      isOffRoute ||
      !stops ||
      stops.length < 2
    ) {
      return;
    }

    const source = {
      lat: parseFloat(stops[0].latitude),
      lng: parseFloat(stops[0].longitude),
    };
    const destination = {
      lat: parseFloat(stops[stops.length - 1].latitude),
      lng: parseFloat(stops[stops.length - 1].longitude),
    };

    let sourceDistanceChange = 0;
    let destDistanceChange = 0;
    for (let i = 1; i < positionHistory.length; i++) {
      const prevPos = positionHistory[i - 1];
      const currPos = positionHistory[i];
      sourceDistanceChange +=
        calculateDistance(currPos, source) - calculateDistance(prevPos, source);
      destDistanceChange +=
        calculateDistance(currPos, destination) -
        calculateDistance(prevPos, destination);
    }

    let newDirection = null;
    if (sourceDistanceChange < 0 && destDistanceChange > 0) {
      newDirection = "dest_to_source";
    } else if (sourceDistanceChange > 0 && destDistanceChange < 0) {
      newDirection = "source_to_dest";
    }

    if (newDirection && newDirection !== pathDirection && vehicle?.device_id) {
      console.log(
        `Updating pathDirection to ${newDirection} based on 15-point trend`
      );
      setPathDirection(newDirection);
      try {
        localStorage.setItem(
          `pathDirection_${vehicle.device_id}`,
          JSON.stringify({
            direction: newDirection,
            timestamp: Date.now(),
            vehicleId: vehicle.device_id,
          })
        );
      } catch (error) {
        console.error("Failed to save to localStorage:", error);
      }
    }

    setPositionHistory((prev) => prev.slice(-DIRECTION_CHECK_THRESHOLD));
  }, [
    positionHistory,
    isMoving,
    isOffRoute,
    stops,
    pathDirection,
    vehicle?.device_id,
    calculateDistance,
  ]);

  useEffect(() => {
    if (
      positionHistory.length < DIRECTION_CHECK_THRESHOLD ||
      directionCheckRef.current ||
      !isMoving ||
      isOffRoute
    ) {
      return;
    }

    directionCheckRef.current = true;

    const currentPosition = positionHistory[positionHistory.length - 1];

    if (pathDirection && isJourneyComplete(currentPosition)) {
      console.log("Journey completed, resetting direction");
      setPathDirection(null);
      setPositionHistory([]);
      if (vehicle?.device_id) {
        localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
      }
    } else if (!pathDirection) {
      const newDirection = checkDirectionChange(positionHistory);
      if (newDirection && vehicle?.device_id) {
        console.log(`Setting initial direction: ${newDirection}`);
        setPathDirection(newDirection);
        try {
          localStorage.setItem(
            `pathDirection_${vehicle.device_id}`,
            JSON.stringify({
              direction: newDirection,
              timestamp: Date.now(),
              vehicleId: vehicle.device_id,
            })
          );
        } catch (error) {
          console.error("Failed to save to localStorage:", error);
        }
      }
    }

    setTimeout(() => {
      directionCheckRef.current = false;
    }, 5000);
  }, [
    positionHistory,
    pathDirection,
    vehicle?.device_id,
    isJourneyComplete,
    checkDirectionChange,
    isMoving,
    isOffRoute,
  ]);

  useEffect(() => {
    if (directions && snappedPosition) {
      const allPoints = extractPathPoints(directions);
      const split = splitRouteAtPosition(allPoints, snappedPosition);
      setRoutePaths(split);
      console.log("Route Paths Updated:", { beforeLength: split.before.length, afterLength: split.after.length }); // Debug log
    }
  }, [directions, snappedPosition, extractPathPoints, splitRouteAtPosition]);

  const fetchStopDetails = async (stop, index, totalStops) => {
    if (!vehicle?.device_id) return { distance: "N/A", eta: "N/A" };
    try {
      const response = await fetch(`${config.baseURL}/distance/time/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vehicleId: vehicle.device_id,
          longitude: stop.longitude,
          latitude: stop.latitude,
          isExtreamPoint: index === 0 || index === totalStops - 1,
        }),
      });
      if (!response.ok) {
        throw new Error(`Distance/Time API error: ${response.status}`);
      }
      const data = await response.json();
      return {
        distance: data.distance || "N/A",
        eta: data.eta || "N/A",
      };
    } catch (error) {
      console.error(`Error fetching details for stop:`, error);
      return { distance: "N/A", eta: "N/A" };
    }
  };

  useEffect(() => {
    if (!stops?.length || !vehicle) return;
    const updateStopDetails = async () => {
      const details = {};
      for (let i = 0; i < stops.length; i++) {
        const stopData = await fetchStopDetails(stops[i], i, stops.length);
        details[stops[i].stopId] = stopData;
      }
      setStopDetails(details);
    };
    updateStopDetails();
    const intervalId = setInterval(
      updateStopDetails,
      STOP_DETAILS_FETCH_INTERVAL
    );
    return () => clearInterval(intervalId);
  }, [stops, vehicle]);

  useEffect(() => {
    return () => {
      if (vehicle?.device_id) {
        localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
      }
      setPathDirection(null);
      setPathInitialized(false);
      setPositionHistory([]);
    };
  }, [vehicle?.device_id]);

  if (loadError) {
    return (
      <div>
        <TitleBar />
        <NavMenu />
        <div style={{ padding: "32px" }}>
          Error loading maps. Please try again later.
        </div>
      </div>
    );
  }

  const RouteHeader = React.memo(({ stops, route, pathDirection }) => {
    if (!stops || stops.length < 2) return null;
    const sourceStop = pathDirection === "dest_to_source" ? stops[stops.length - 1] : stops[0];
    const destinationStop = pathDirection === "dest_to_source" ? stops[0] : stops[stops.length - 1];
    const directionLabel =
    pathDirection === "dest_to_source" ? (
      <EastOutlinedIcon sx={{ width: 40, height: 40 }}/>
    ) : pathDirection === "source_to_dest" ? (
      <EastOutlinedIcon sx={{ width: 40, height: 40 }}/>
    ) : null;
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 10px",
            backgroundColor: "#183059",
            color: "white",
            fontWeight: "bold",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: "10px", fontSize: "36px" }}>🟢</span>
            <h1 style={{ fontSize: "48px" }}>{sourceStop.name || "Unknown"}</h1>
          </div>
          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.2)",
              padding: "10px 10px",
              borderRadius: "4px",
              // fontSize: "42px",
            }}
          >
            {directionLabel}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <h1 style={{ fontSize: "48px" }}>{destinationStop.name || "Unknown"}</h1>
            <span style={{ marginLeft: "10px", fontSize: "36px" }}>🏁</span>
          </div>
        </div>
        {isReconnecting && (
          <div
            style={{
              backgroundColor: "#FFD700",
              color: "black",
              textAlign: "center",
              padding: "5px",
              fontWeight: "bold",
            }}
          >
            Reconnecting...
          </div>
        )}
      </div>
    );
  });

  const OfflineIndicator = () => (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "rgba(255, 0, 0, 0.9)",
        color: "white",
        padding: "20px 40px",
        borderRadius: "8px",
        zIndex: 1000,
        textAlign: "center",
        boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
      }}
    >
      <div style={{ fontSize: "24px", fontWeight: "bold" }}>
        No Internet Connection
      </div>
      <div style={{ fontSize: "16px", marginTop: "10px" }}>
        Please check your network
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{ display: "flex", flexDirection: "row-reverse", gap: "10px" }}
      >
        <>
          {!route || !stops ? (
            <div style={{ marginBottom: "20px" }}>
              <CircularProgress />
            </div>
          ) : (
            <NextStopTableScreenScreen
              details={route}
              stops={visibleStops}
              stopDetails={stopDetails}
              isInbound={pathDirection === "dest_to_source"}
              currentPosition={snappedPosition}
              routeCoordinates={route?.routeCoordinates}
            />
          )}
          {!isOnline && <OfflineIndicator />}
          {!isLoaded ? (
            <div
              style={{
                height: "68.5vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div
              style={{ width: "73vw", height: "98vh", position: "relative" }}
            >
              {route && stops && (
                <RouteHeader
                  stops={stops}
                  route={route}
                  pathDirection={pathDirection}
                />
              )}
              <div
                style={{
                  position: "absolute",
                  top: "10%",
                  left: "0",
                  width: "100%",
                  height: "90%",
                }}
              >
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={
                    position ||
                    (stops?.length
                      ? {
                          lat: parseFloat(stops[0].latitude),
                          lng: parseFloat(stops[0].longitude),
                        }
                      : null)
                  }
                  zoom={16}
                  onLoad={onLoad}
                  onUnmount={onUnmount}
                  options={{
                    styles: mapStyles,
                    disableDefaultUI: true,
                    zoomControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    scrollwheel: false,
                    draggable: false,
                    gestureHandling: "none",
                    keyboardShortcuts: false,
                    minZoom: 16.5,
                    maxZoom: 30,
                    animatedZoom: true,
                    maxZoomRectangleSize: 8000,
                  }}
                >
                  {!pathDirection && fullPath.length > 0 && (
                    <Polyline
                      path={fullPath}
                      options={{
                        strokeColor: "#808080",
                        strokeOpacity: 1.0,
                        strokeWeight: 10,
                      }}
                    />
                  )}
                  {pathDirection && fullPath.length > 0 && (
                    <>
                      {routePaths.before.length > 0 && (
                        <Polyline
                          path={routePaths.before}
                          options={{
                            strokeColor: "#808080",
                            strokeOpacity: 1.0,
                            strokeWeight: 6,
                          }}
                        />
                      )}
                      {routePaths.after.length > 0 && (
                        <Polyline
                          path={routePaths.after}
                          options={{
                            strokeColor: route?.routeColor || "#2196F3",
                            strokeOpacity: 1.0,
                            strokeWeight: 10,
                          }}
                        />
                      )}
                    </>
                  )}
                  {!isMoving && stoppedDuration > 60 && position && (
                    <Marker
                      position={position}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 16,
                        fillColor: "#FF0000",
                        fillOpacity: 0.7,
                        strokeWeight: 2,
                        strokeColor: "#FFFFFF",
                      }}
                      title={`Vehicle stopped for ${Math.floor(
                        stoppedDuration / 60
                      )} minutes`}
                    />
                  )}
                  {visibleStops.map((stop, index) =>
                    stop.name && stop.name.trim() !== "" ? (
                      <React.Fragment key={`stop-${stop.id}-${index}`}>
                        <OverlayView
                          position={{
                            lat: parseFloat(stop.latitude),
                            lng: parseFloat(stop.longitude),
                          }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <div
                            style={{
                              position: "relative",
                              top: "-182px",
                              left: "0px",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                width: "2px",
                                backgroundColor: "blue",
                                height: "150px",
                                top: "10px",
                                left: "50%",
                                transform: "translateX(-50%)",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "white",
                                color:
                                  index === 0
                                    ? "red"
                                    : index === stops.length - 1
                                    ? "blue"
                                    : "black",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                fontSize: "24px",
                                fontWeight: "500",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                whiteSpace: "nowrap",
                                zIndex: 1000,
                              }}
                            >
                              {stop.name}
                            </div>
                          </div>
                        </OverlayView>
                        <Marker
                          key={`marker-${stop.id}-${index}`}
                          position={{
                            lat: parseFloat(stop.latitude),
                            lng: parseFloat(stop.longitude),
                          }}
                        />
                      </React.Fragment>
                    ) : null
                  )}
                  {position && isFinite(position.lat) && isFinite(position.lng) && (
                    <React.Fragment>
                      <OverlayView
                        position={position}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div style={{ position: "relative", top: "-50px" }}>
                          <div
                            style={{
                              position: "absolute",
                              width: "2px",
                              backgroundColor: "#20A8E0",
                              height: "80px",
                              top: "-30px",
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              bottom: "30px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              color: "rgb(0, 0, 0)",
                              fontSize: "24px",
                              fontWeight: 600,
                              fontFamily: "Arial, sans-serif",
                              lineHeight: "14.06px",
                              padding: "10px",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              backgroundColor: "#20A8E0",
                              borderRadius: "8px",
                              zIndex: 1000,
                              border: "1px solid black",
                            }}
                          >
                            {vehicle?.name || "Vehicle"}
                          </div>
                        </div>
                      </OverlayView>
                      <OverlayView
                        position={position}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          style={{
                            width: pathDirection ? "60px" : "80px",
                            height: pathDirection ? "60px" : "80px",
                            position: "relative",
                            transform: `translate(${pathDirection ? "-20px" : "-30px"}, ${pathDirection ? "-24px" : "-30px"}) rotate(${heading - 1}deg) ${pathDirection === 'dest_to_source' ? 'scaleX(-1)' : ''}`,
                            transition: "transform 0.3s ease-in-out",
                            backgroundImage: `url(${pathDirection ? ActiveVehicleIcon : DefaultVehicleIcon})`,
                            backgroundSize: "contain",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                          }}
                        />
                      </OverlayView>
                    </React.Fragment>
                  )}
                </GoogleMap>
              </div>
            </div>
          )}
        </>
      </div>
    </>
  );
};

export default VehicleScreen;
